/**
 * An absolute href for an AI-engine source citation, or null when the value
 * isn't navigable.
 *
 * Sources arrive scheme-less in some rows (Gemini stored the bare `web.domain`
 * before the 2026-07 redirect-resolution fix), and a scheme-less string in
 * `href` / `window.open` is a RELATIVE path — it navigated to
 * `/brand/<domain>` inside the app instead of opening the site. So force a
 * scheme, and reject anything that isn't a plain http(s) host: `javascript:`
 * payloads, and site-name labels like "State of Digital Publishing" that never
 * resolved to a URL.
 */
/**
 * True for a Gemini grounding-redirect wrapper. Its URL is an opaque
 * click-tracker, so it's fine to navigate to but must never be SHOWN to the
 * user — display the accompanying domain instead.
 */
export function isGroundingWrapper(value?: string): boolean {
  return (value || '').includes('vertexaisearch.cloud.google.com');
}

/**
 * Strip scheme + path + `www.` to a bare hostname. Tolerates the three
 * shapes the rest of the app stores domains in: full URL
 * (`https://www.foo.fr/`), `www.`-prefixed host, and bare host.
 */
export function bareHost(value?: string): string {
  if (!value) return '';
  let h = value.toLowerCase().trim();
  h = h.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  if (h.startsWith('www.')) h = h.slice(4);
  return h;
}

export function sourceHref(value?: string): string | null {
  const raw = (value || '').trim();
  if (!raw) return null;
  if (/^(?!https?:)[a-z][a-z0-9+.-]*:/i.test(raw)) return null;
  try {
    const u = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    // A real host has a dot and no whitespace; `new URL` happily accepts labels.
    if (!u.hostname.includes('.') || /\s/.test(u.hostname)) return null;
    return u.toString();
  } catch {
    return null;
  }
}
